import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function Vip() {
  const [rows, setRows] = useState([]);
  const [uid, setUid] = useState("");
  const [type, setType] = useState("lifetime");
  const [exp, setExp] = useState("");
  const toast = useToast();
  const load = () => api.get("/admin/vip/list").then(({ data }) => setRows(data.vip_users || []));
  useEffect(() => { load(); }, []);
  const assign = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/vip/assign", { user_id: uid, vip_type: type, expires_at: exp || null });
      toast.success("Assigned"); setUid(""); setExp(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm(`Revoke VIP from ${id}?`)) return;
    try { await api.delete(`/admin/vip/${id}`); toast.success("Revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-6 fade-up">
      <form onSubmit={assign} className="glass-card p-5 space-y-3" data-testid="vip-assign-form">
        <h3 className="label">Assign VIP</h3>
        <Field label="User ID"><input className="input" required value={uid} onChange={(e) => setUid(e.target.value)} data-testid="vip-user-id" /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Type"><input className="input" value={type} onChange={(e) => setType(e.target.value)} /></Field>
          <Field label="Expires (ISO, optional)"><input className="input" type="datetime-local" value={exp} onChange={(e) => setExp(e.target.value)} /></Field>
        </div>
        <button className="btn" data-testid="vip-assign-submit">Assign</button>
      </form>
      {/* Phones: card list — the 5-column table pushes Revoke off-screen. */}
      <ul className="glass-card divide-y divide-white/5 sm:hidden" data-testid="vip-list-mobile">
        {rows.map((r) => (
          <li key={`m-${r.user_id}`} className="p-3 space-y-1.5" data-testid={`vip-card-${r.user_id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="truncate">
                {r.discord_username ? `@${r.discord_username}` : (r.web_username || r.user_id)}
              </span>
              <button onClick={() => remove(r.user_id)} className="btn btn-sm btn-danger shrink-0">Revoke</button>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs text-[var(--muted)]">
              <span className="badge text-[var(--rarity-legendary)] border-[var(--rarity-legendary)]">{r.vip_type}</span>
              <span>Expires: {r.expires_at || "never"}</span>
            </div>
            <div className="font-mono text-[10px] text-[var(--muted)] break-all">{r.user_id}</div>
          </li>
        ))}
        {rows.length === 0 && <li className="p-6 text-center text-[var(--muted)]">No VIPs.</li>}
      </ul>

      <div className="glass-card overflow-x-auto hidden sm:block">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-[var(--muted)]"><tr><th className="text-left p-3">User</th><th className="text-left p-3">Type</th><th className="text-left p-3">Assigned</th><th className="text-left p-3">Expires</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} className="border-t border-white/5" data-testid={`vip-row-${r.user_id}`}>
                <td className="p-3 align-top">
                  {(r.discord_username || r.web_username) ? (
                    <div className="flex flex-col gap-0.5">
                      {r.discord_username && (
                        <span className="inline-flex items-center gap-1.5" data-testid={`vip-discord-${r.user_id}`}>
                          <span className="text-[9px] text-indigo-300/80 uppercase tracking-wider">Discord</span>
                          <span>@{r.discord_username}</span>
                        </span>
                      )}
                      {r.web_username && (
                        <span className="inline-flex items-center gap-1.5" data-testid={`vip-web-${r.user_id}`}>
                          <span className="text-[9px] text-teal-300/80 uppercase tracking-wider">Web</span>
                          <span className="truncate max-w-[220px]">{r.web_username}</span>
                        </span>
                      )}
                      <span className="text-[9px] text-[var(--muted)] font-mono break-all">{r.user_id}</span>
                    </div>
                  ) : (
                    <span className="font-mono break-all">{r.user_id}</span>
                  )}
                </td>
                <td className="p-3"><span className="badge text-[var(--rarity-legendary)] border-[var(--rarity-legendary)]">{r.vip_type}</span></td>
                <td className="p-3 text-[var(--muted)]">{r.assigned_at}</td>
                <td className="p-3 text-[var(--muted)]">{r.expires_at || "—"}</td>
                <td className="p-3 text-right"><button onClick={() => remove(r.user_id)} className="btn btn-sm btn-danger">Revoke</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[var(--muted)]">No VIPs.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
